import {
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  DownOutlined,
  FileAddOutlined,
  FolderOutlined,
  StarFilled,
  StarOutlined,
  TagOutlined
} from "@ant-design/icons"
import { Button, Dropdown, Flex, Popconfirm, Tooltip, Typography } from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  renderDocumentTypeDecoration,
  renderStatusDecoration
} from "../helpers/render-metadata-decoration.tsx"

const { Text } = Typography

interface MetadataOption {
  label: string
  value: string
  /** Document Type only — the admin-configured icon name, when set. */
  icon?: string
}

interface BulkActionBarProps {
  count: number
  onMove?: () => void
  onAddToAlbum?: () => void
  onDownload: () => void
  onDelete: () => void
  onClear: () => void
  canDelete: boolean
  /** Present only while at least one selected file is a favoritable document/photo. */
  onFavorite?: () => void
  onUnfavorite?: () => void
  allFavorited?: boolean
  /** Present only while at least one selected file is an editable document. */
  statusOptions?: MetadataOption[]
  /** True whenever at least one selected file is an editable document — set
   *  even when `statusOptions` comes back empty, so the bar can tell "no
   *  status editing applies here" (hidden) apart from "these files share no
   *  common next status" (shown, disabled, explained). */
  statusApplicable?: boolean
  onChangeStatus?: (value: string) => void
  documentTypeOptions?: MetadataOption[]
  onChangeDocumentType?: (value: string) => void
}

export function BulkActionBar({
  count,
  onMove,
  onAddToAlbum,
  onDownload,
  onDelete,
  onClear,
  canDelete,
  onFavorite,
  onUnfavorite,
  allFavorited,
  statusOptions,
  statusApplicable,
  onChangeStatus,
  documentTypeOptions,
  onChangeDocumentType
}: BulkActionBarProps) {
  const { t } = useTranslation()

  if (!count) return null

  const statusMenuItems: MenuItemType[] = (statusOptions ?? []).map((option) => ({
    key: option.value,
    label: (
      <Flex align="center" gap={6}>
        {renderStatusDecoration(option.value)}
        {option.label}
      </Flex>
    ),
    onClick: () => onChangeStatus?.(option.value)
  }))

  const documentTypeMenuItems: MenuItemType[] = (documentTypeOptions ?? []).map(
    (option) => ({
      key: option.value,
      label: (
        <Flex align="center" gap={6}>
          {renderDocumentTypeDecoration(option.value, option.icon)}
          {option.label}
        </Flex>
      ),
      onClick: () => onChangeDocumentType?.(option.value)
    })
  )

  return (
    <Flex
      align="center"
      justify="space-between"
      wrap="wrap"
      gap="small"
      className="rounded-md border border-border bg-primary/5 px-3 py-2"
    >
      <Flex align="center" gap="small">
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClear}
        />
        <Text strong>
          {t("files.bulk.selected", { count })}
        </Text>
      </Flex>
      <Flex gap="small" wrap="wrap">
        {onMove && (
          <Button icon={<FolderOutlined />} onClick={onMove}>
            {t("files.actions.move")}
          </Button>
        )}
        {onAddToAlbum && (
          <Button icon={<FileAddOutlined />} onClick={onAddToAlbum}>
            {t("files.actions.addToAlbum")}
          </Button>
        )}
        {documentTypeMenuItems.length > 0 && onChangeDocumentType && (
          <Dropdown menu={{ items: documentTypeMenuItems }} trigger={["click"]}>
            <Button icon={<TagOutlined />}>
              {t("files.bulk.documentType")} <DownOutlined />
            </Button>
          </Dropdown>
        )}
        {statusApplicable && onChangeStatus && (
          <Tooltip
            title={statusMenuItems.length === 0 ? t("files.bulk.statusUnavailable") : undefined}
          >
            <Dropdown
              menu={{ items: statusMenuItems }}
              trigger={statusMenuItems.length > 0 ? ["click"] : []}
              disabled={statusMenuItems.length === 0}
            >
              <Button icon={<TagOutlined />} disabled={statusMenuItems.length === 0}>
                {t("files.bulk.status")} <DownOutlined />
              </Button>
            </Dropdown>
          </Tooltip>
        )}
        {allFavorited
          ? onUnfavorite && (
              <Button icon={<StarFilled className="text-warning" />} onClick={onUnfavorite}>
                {t("files.bulk.unfavorite")}
              </Button>
            )
          : onFavorite && (
              <Button icon={<StarOutlined />} onClick={onFavorite}>
                {t("files.bulk.favorite")}
              </Button>
            )}
        <Button icon={<DownloadOutlined />} onClick={onDownload}>
          {t("files.actions.download")}
        </Button>
        {canDelete && (
          <Popconfirm
            title={t("files.bulk.deleteConfirm", { count })}
            okButtonProps={{ danger: true }}
            onConfirm={onDelete}
          >
            <Button danger icon={<DeleteOutlined />}>
              {t("files.actions.moveToTrash")}
            </Button>
          </Popconfirm>
        )}
      </Flex>
    </Flex>
  )
}
